import { useState, useEffect, useCallback, useMemo } from 'react'
import { ScrollNavigationConfig, ScrollNavigationResult } from './typings'

const PAGE_BUTTONS_WIDTH = 90

/**
 * Custom hook that encapsulates all horizontal scroll/pagination logic
 * for the progress widget. Handles:
 * - Calculating whether pagination buttons are needed
 * - Computing segment widths based on available space
 * - Scroll-to-index / scroll-to-edge behavior
 * - Snap-to-segment scrolling
 * - Mobile mode where page buttons are hidden (swipe only)
 */
export function useScrollNavigation(
  config: ScrollNavigationConfig,
): ScrollNavigationResult {
  const {
    containerWidth,
    itemCount,
    segmentMinWidth,
    segmentSpacing,
    height,
    marginToNextElement,
    scrollTo = 0,
    snap = true,
    hidePageButtons = false,
  } = config

  const [scrollAmount, setScrollAmount] = useState(0)

  // Negative margin so chevron shapes overlap
  const marginLeft = useMemo(
    () => -Math.ceil(height / 2) + segmentSpacing,
    [height, segmentSpacing],
  )

  // Total margin across all items
  const totalMarginAmount = useMemo(
    () => Math.max(0, itemCount - 1) * marginLeft + marginToNextElement,
    [itemCount, marginLeft, marginToNextElement],
  )

  // Whether the items overflow the container
  const overflows = useMemo(
    () =>
      containerWidth ? itemCount * segmentMinWidth > containerWidth : false,
    [containerWidth, itemCount, segmentMinWidth],
  )

  // On mobile we always hide the buttons but still need to know if content overflows
  const showPageButtons = overflows && !hidePageButtons

  // Effective page buttons width (0 when hidden — gives segments more room)
  const effectivePageButtonsWidth = showPageButtons ? PAGE_BUTTONS_WIDTH : 0

  // Width of each segment, expanding to fill available space
  const segmentWidth = useMemo(() => {
    if (!containerWidth) return undefined
    return Math.max(
      segmentMinWidth,
      (containerWidth - totalMarginAmount - effectivePageButtonsWidth) /
        itemCount,
    )
  }, [
    containerWidth,
    effectivePageButtonsWidth,
    itemCount,
    segmentMinWidth,
    totalMarginAmount,
  ])

  // Effective width including negative margin
  const segmentEffectiveWidth = segmentWidth ? segmentWidth + marginLeft : 0

  // Minimum (most-negative) scroll value
  const minScrollAmount = useMemo(() => {
    if (!containerWidth || !segmentWidth) return 0
    const totalContentWidth =
      segmentWidth * itemCount + totalMarginAmount + effectivePageButtonsWidth
    if (totalContentWidth <= containerWidth) return 0
    return -(totalContentWidth - containerWidth)
  }, [
    containerWidth,
    effectivePageButtonsWidth,
    itemCount,
    segmentWidth,
    totalMarginAmount,
  ])

  // Clamp scroll when container resizes or items change
  useEffect(() => {
    if (!overflows) {
      setScrollAmount(0)
      return
    }
    setScrollAmount((prev) => {
      if (prev < minScrollAmount) return minScrollAmount
      return prev
    })
  }, [containerWidth, itemCount, minScrollAmount, overflows])

  // Handle scrollTo prop changes
  useEffect(() => {
    if (scrollTo === undefined || scrollTo === null) return

    if (typeof scrollTo === 'number') {
      const targetScroll = Math.max(
        minScrollAmount,
        -(scrollTo * segmentEffectiveWidth) +
          (containerWidth ? containerWidth / 2 : 0) -
          segmentEffectiveWidth / 2,
      )
      setScrollAmount(Math.min(targetScroll, 0))
    } else if (scrollTo === 'left') {
      setScrollAmount(0)
    } else if (scrollTo === 'right') {
      setScrollAmount(minScrollAmount)
    }
  }, [
    containerWidth,
    itemCount,
    minScrollAmount,
    scrollTo,
    segmentEffectiveWidth,
  ])

  // Scroll left (towards the beginning — less negative scroll)
  const handleScrollLeft = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation()
      if (!snap) {
        setScrollAmount((prev) => Math.min(0, prev + segmentEffectiveWidth))
        return
      }
      // How far past the last snap boundary we are (always positive)
      const remainder = Math.abs(scrollAmount % segmentEffectiveWidth)
      // If at (or very near) a boundary, jump a full segment; otherwise snap to nearest
      const offset = remainder < 1 ? segmentEffectiveWidth : remainder
      setScrollAmount((prev) => Math.min(0, prev + offset))
    },
    [scrollAmount, segmentEffectiveWidth, snap],
  )

  // Scroll right (towards the end — more negative scroll)
  const handleScrollRight = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation()
      if (!snap) {
        setScrollAmount((prev) =>
          Math.max(minScrollAmount, prev - segmentEffectiveWidth),
        )
        return
      }
      // Distance to the next snap boundary to the right
      const remainder = Math.abs(scrollAmount % segmentEffectiveWidth)
      const distanceToNext =
        remainder < 1
          ? segmentEffectiveWidth
          : segmentEffectiveWidth - remainder
      // Guard against floating-point near-zero producing a tiny step
      const offset = distanceToNext < 1 ? segmentEffectiveWidth : distanceToNext
      setScrollAmount((prev) => Math.max(minScrollAmount, prev - offset))
    },
    [minScrollAmount, scrollAmount, segmentEffectiveWidth, snap],
  )

  return {
    scrollAmount,
    showPageButtons,
    segmentWidth,
    marginLeft,
    minScrollAmount,
    canScrollLeft: scrollAmount < 0,
    canScrollRight: scrollAmount > minScrollAmount,
    handleScrollLeft,
    handleScrollRight,
  }
}
